import { useState, useRef } from 'react';
import { Phone, Mail, MapPin, MessageCircle } from 'lucide-react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useSEO, localKeywords } from '@/hooks/useSEO';
import { useStructuredData } from '@/hooks/useStructuredData';
import { buildContactPageSchema, buildBreadcrumbSchema } from '@/lib/seo/schemas';
import { useMemo } from 'react';
import { COMPANY } from '@/config/company';

export default function Contact() {
  useSEO({
    title: 'İletişim & Showroom | Mayi Mimarlık Diyarbakır Kayapınar',
    description: `Diyarbakır seramik ve vitrifiye showroom: ${COMPANY.address}. Telefon ${COMPANY.phoneDisplay}. Creavit, Duratiles, QUA, Kalekim, Vitra Fix teklif ve bayilik için bize ulaşın.`,
    keywords: localKeywords(
      'mayi mimarlık iletişim',
      'diyarbakır seramik mağaza adresi',
      'kayapınar vitrifiye showroom',
      'çeysa plaza seramik',
      'diyarbakır yapı malzemeleri telefon'
    ),
    canonical: 'https://mayimimarlik.com/iletisim',
  });

  const schema = useMemo(() => {
    const contact = buildContactPageSchema();
    const breadcrumb = buildBreadcrumbSchema([
      { name: 'Ana Sayfa', url: 'https://mayimimarlik.com/' },
      { name: 'İletişim', url: 'https://mayimimarlik.com/iletisim' },
    ]);
    const { '@context': _a, ...ct } = contact;
    const { '@context': _b, ...crumb } = breadcrumb;
    return { '@context': 'https://schema.org', '@graph': [ct, crumb] };
  }, []);

  useStructuredData('schema-contact', schema);
  const [form, setForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [recaptchaValue, setRecaptchaValue] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message || !recaptchaValue) return;

    const body = `Ad Soyad: ${form.name}\nE-posta: ${form.email}\n\nMesaj:\n${form.message}`;
    window.location.href = `mailto:info@mayimimarlik.com?subject=${encodeURIComponent(form.subject || 'Web Sitesi İletişim Formu')}&body=${encodeURIComponent(body)}`;
  };

  return (
    <div className="page-container" style={{ paddingTop: 'calc(var(--header-height) + var(--spacing-3xl))', minHeight: '80vh' }}>
      <div className="container">
        <h1 className="text-4xl mb-md">İletişim & Showroom</h1>
        <div className="section-divider mb-xl"></div>
        
        <div className="flex flex-col md-flex-row gap-4xl" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--spacing-4xl)' }}>
          
          {/* Contact Info */}
          <div>
            <h2 className="text-2xl mb-lg">Bize Ulaşın</h2>
            <p className="text-secondary mb-xl">Bayilik talepleriniz, proje teklifleri veya showroom ziyareti için bizimle iletişime geçebilirsiniz.</p>
            
            <ul className="flex flex-col gap-lg mb-xl">
              <li className="flex items-start gap-md">
                <div className="flex items-center justify-center bg-tertiary flex-shrink-0" style={{ width: 48, height: 48, borderRadius: '50%' }}>
                  <MapPin className="text-accent" />
                </div>
                <div>
                  <h4 className="font-medium">Adres</h4>
                  <span className="text-secondary text-sm">{COMPANY.address}</span>
                </div>
              </li>
              <li className="flex items-center gap-md">
                <div className="flex items-center justify-center bg-tertiary flex-shrink-0" style={{ width: 48, height: 48, borderRadius: '50%' }}>
                  <Phone className="text-accent" />
                </div>
                <div>
                  <h4 className="font-medium">Telefon</h4>
                  <a href={`tel:${COMPANY.phoneTel}`} className="text-secondary text-sm hover-accent">{COMPANY.phoneDisplay}</a>
                </div>
              </li>
              <li className="flex items-center gap-md">
                <div className="flex items-center justify-center bg-tertiary flex-shrink-0" style={{ width: 48, height: 48, borderRadius: '50%' }}>
                  <MessageCircle className="text-accent" />
                </div>
                <div>
                  <h4 className="font-medium">WhatsApp</h4>
                  <a href={COMPANY.whatsappUrl} target="_blank" rel="noopener noreferrer" className="text-secondary text-sm hover-accent">{COMPANY.phoneDisplay}</a>
                </div>
              </li>
              <li className="flex items-center gap-md">
                <div className="flex items-center justify-center bg-tertiary" style={{ width: 48, height: 48, borderRadius: '50%' }}>
                  <Mail className="text-accent" />
                </div>
                <div>
                  <h4 className="font-medium">E-posta</h4>
                  <a href={`mailto:${COMPANY.email}`} className="text-secondary text-sm hover-accent">{COMPANY.email}</a>
                </div>
              </li>
            </ul>
          </div>
          
          {/* Contact Form Placeholder */}
          <div className="glass" style={{ padding: 'var(--spacing-2xl)', borderRadius: 'var(--radius-lg)' }}>
            <h3 className="text-2xl mb-lg">Mesaj Gönderin</h3>
            <form className="flex flex-col gap-md" onSubmit={handleSubmit}>
              <input 
                type="text" 
                placeholder="Ad Soyad" 
                className="form-input" 
                style={inputStyle} 
                required 
                value={form.name} 
                onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} 
              />
              <input 
                type="email" 
                placeholder="E-posta Adresiniz" 
                className="form-input" 
                style={inputStyle} 
                required 
                value={form.email} 
                onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))} 
              />
              <input 
                type="text" 
                placeholder="Konu (Örn: Bayilik Talebi)" 
                className="form-input" 
                style={inputStyle} 
                value={form.subject} 
                onChange={(e) => setForm(f => ({ ...f, subject: e.target.value }))} 
              />
              <textarea 
                placeholder="Mesajınız" 
                rows={4} 
                className="form-input" 
                style={{ ...inputStyle, resize: 'vertical' }} 
                required 
                value={form.message} 
                onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))} 
              ></textarea>
              
              <div style={{ marginTop: '0.5rem' }}>
                <ReCAPTCHA
                  sitekey="6LfYdKssAAAAAKtxpV5zqmLX9P6NhTorvJRMj5jc"
                  onChange={(val) => setRecaptchaValue(val)}
                />
              </div>

              <button 
                type="submit" 
                className="btn btn-primary mt-sm" 
                style={{ width: '100%', opacity: (!form.name || !form.email || !form.message || !recaptchaValue) ? 0.6 : 1 }}
                disabled={!form.name || !form.email || !form.message || !recaptchaValue}
              >
                Gönder
              </button>
            </form>
          </div>
        </div>

        {/* Map Section */}
        <div 
          style={{ 
            marginTop: 'calc(var(--spacing-4xl) * 1.5)', 
            borderRadius: 'var(--radius-lg)', 
            overflow: 'hidden', 
            height: '500px', 
            width: '100%',
            border: '1px solid var(--color-border)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }}
        >
          <iframe 
            src={COMPANY.mapsEmbedUrl}
            width="100%" 
            height="100%"
            style={{ border: 0 }}
            loading="lazy"
            title="Mayi Mimarlık Konumu"
          />
        </div>
      </div>
    </div>
  );
}

const inputStyle = {
  width: '100%',
  padding: 'var(--spacing-md)',
  backgroundColor: 'rgba(255,255,255,0.05)',
  border: '1px solid var(--border-color)',
  borderRadius: 'var(--radius-sm)',
  color: 'var(--text-primary)',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border-color 0.2s ease'
};
